import React, { useEffect, useState } from "react";
import InputField from "../InputField";
import TextArea from "../TextArea";
import ComboBox from "../ComboBox";

import RatingStars from "../RatingStars";
/**
 * Represents the props for the DynamicFormValue component.
 */
interface DynamicFormValueProps {
  /**
   * The value of the DynamicForm component.
   */
  value: DynamicFormProps;

  children?: React.ReactNode;
}

/**
 * Represents the configuration for a form field.
 */
interface FormFieldConfig {
  /**
   * The unique identifier for the form field.
   */
  id: string;

  /**
   * The options for the form field.
   */
  options?: { value: string; label: string }[];

  /**
   * The label for the form field.
   */
  label: string;

  /**
   * The placeholder text for the form field.
   */
  placeholder?: string;

  /**
   * The type of the form field.
   * Can be one of: "text", "number", or "textarea".
   */
  type: "text" | "number" | "password" | "textarea" | "combobox" | "stars";

  className?: string;

  inputClassName?: string;

  initialValue?: string;
  validate?: ((value: any, allValues: { [key: string]: any }) => string | null)[];
}

interface DynamicFormProps {
  /**
   * The unique identifier for the form.
   */
  id: string;

  /**
   * An array of field configurations for the form.
   */
  fields: FormFieldConfig[];

  /**
   * A callback function that will be called when the form is submitted.
   * @param id - The identifier of the form.
   * @param values - An object containing the form field values.
   */
  onSubmit: (id: string, values: { [key: string]: any }) => void;
}

/**
 * A dynamic form component that renders form fields based on the provided configuration.
 *
 * @component
 * @param {DynamicFormValueProps} props - The props for the DynamicForm component.
 * @param {DynamicFormConfig} props.value - The configuration object for the dynamic form.
 * @returns {JSX.Element} The rendered DynamicForm component.
 */
const DynamicForm: React.FC<DynamicFormValueProps> = ({ children, value }) => {
  const { id, fields, onSubmit } = value;
  const initializeFormData = () => {
    const initialData: { [key: string]: any } = {};
    fields.forEach((field) => {
      if (field.initialValue !== undefined) {
        initialData[field.id] = field.initialValue;
      }
    });
    return initialData;
  };

  const initializeErrors = () => {
    const initialErrors: { [key: string]: string | null } = {};
    fields.forEach((field) => {
      initialErrors[field.id] = null;
    });
    return initialErrors;
  };

  const [formData, setFormData] = useState<{ [key: string]: any }>(
    initializeFormData
  );
  const [errors, setErrors] = useState<{ [key: string]: string | null }>(
    initializeErrors
  );

  useEffect(() => {
    setFormData(initializeFormData());
  }, [fields]);

  const handleChange = (fieldId: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [key: string]: string | null } = {};
    fields.forEach((field) => {
      if (field.validate) {
        const fieldValidators = field.validate;
        const error = fieldValidators
          .map((validator) => validator(formData[field.id], formData))
          .find((error) => error !== null) || null;
        newErrors[field.id] = error;
      }
    });
    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== null);

    if (!hasErrors) {
      onSubmit(id, formData);
    }
  };

  const renderInputField = (field: FormFieldConfig, index: number) => {
    return (
      <div key={index}>
        <InputField
          value={{
            type: field.type as "text" | "number",
            label: field.label,
            text: formData[field.id],
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(field.id, e.target.value),
            placeholder: field.placeholder,
            className: field.className,
            inputClassName: field.inputClassName,
            labelClassName: "text-sm",
          }}
        />
        {errors[field.id] && <div className="text-red-500">{errors[field.id]}</div>}
      </div>
    );
  };

  const renderTextArea = (field: FormFieldConfig, index: number) => {
    return (
      <div key={index}>
        <TextArea
          value={{
            label: field.label,
            text: formData[field.id] || "",
            onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleChange(field.id, e.target.value),
            placeholder: field.placeholder,
            className: field.className,
            inputClassName: field.inputClassName,
          }}
        />
        {errors[field.id] && <div className="text-red-500">{errors[field.id]}</div>}
      </div>
    );
  };

  const renderComboBox = (field: FormFieldConfig, index: number) => {
    return (
      <div key={index}>
        <ComboBox
          value={{
            label: field.label,
            options: field.options || [],
            selectedValue: formData[field.id] || "",
            onChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
              handleChange(field.id, e.target.value),
            placeholder: field.placeholder,
          }}
        />
        {errors[field.id] && <div className="text-red-500">{errors[field.id]}</div>}
      </div>
    );
  };

  const renderStars = (field: FormFieldConfig, index: number) => {
    return (
      <div key={index}>
        <RatingStars
          value={{
            onChange: (e: number) => {
              handleChange(field.id, e);
            },
            selectedValue: formData[field.id] || "",
            fillColor: "#2F9CAB",
            emptyColor: "#ccc",
            fontSize: "2rem",
          }}
        />
        {errors[field.id] && <div className="text-red-500">{errors[field.id]}</div>}
      </div>
    );
  };

  const renderMapping = {
    text: renderInputField,
    password: renderInputField,
    number: renderInputField,
    textarea: renderTextArea,
    combobox: renderComboBox,
    stars: renderStars,
  };

  const renderFormularField = (fields: FormFieldConfig[]) => {
    return fields.map((field, index: number) => {
      const renderField = renderMapping[field.type];
      return renderField ? renderField(field, index) : null;
    });
  };

  const renderActionField = () => {
    return (
      <button
        type="submit"
        className="form-button"
      >
        Submit
      </button>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {children ? children : renderFormularField(fields)}
      {renderActionField()}
    </form>
  );
};

export default DynamicForm;
